import { useMemo } from "react"
import { ArrowLeft, BookOpen } from "lucide-react"
import { Link } from "react-router-dom"
import glossaryMarkdown from "../../../docs/GLOSSARY.md?raw"

/**
 * The team's metric glossary, on its own route — same separation as /ops.
 *
 * The markdown is bundled at BUILD time via `?raw`, not fetched: this page is
 * what someone opens mid-argument about what a number means, so it renders
 * from memory with no round-trip, no auth handshake and no spinner. It cannot
 * go stale relative to the repo either — the doc IS the source, and editing
 * `docs/GLOSSARY.md` is the whole deploy.
 *
 * The renderer below is deliberately small: headings, paragraphs, bullets,
 * tables, fences, rules and three inline marks. That is every construct the
 * doc actually uses, and a markdown library would be ~40kB to render a file
 * we control the syntax of.
 */

const HEADING_RE = /^(#{1,6})\s+(.*)$/
const TABLE_ROW_RE = /^\s*\|(.*)\|\s*$/
const TABLE_DIVIDER_RE = /^\s*\|[\s:|-]+\|\s*$/
const BULLET_RE = /^(\s*)[-*]\s+(.*)$/
const ORDERED_RE = /^(\s*)(\d+)\.\s+(.*)$/

type Block =
  | { kind: "heading"; level: number; text: string; slug: string }
  | { kind: "para"; lines: string[] }
  | { kind: "list"; ordered: boolean; items: { depth: number; text: string }[] }
  | { kind: "table"; head: string[]; rows: string[][] }
  | { kind: "code"; text: string }
  | { kind: "rule" }

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
}

function cells(line: string) {
  const inner = line.match(TABLE_ROW_RE)?.[1] ?? ""
  return inner.split("|").map((c) => c.trim())
}

function parse(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n")
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i += 1
      continue
    }

    if (line.startsWith("```")) {
      const body: string[] = []
      i += 1
      while (i < lines.length && !lines[i].startsWith("```")) {
        body.push(lines[i])
        i += 1
      }
      i += 1 // closing fence
      blocks.push({ kind: "code", text: body.join("\n") })
      continue
    }

    if (/^(-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
      blocks.push({ kind: "rule" })
      i += 1
      continue
    }

    const heading = line.match(HEADING_RE)
    if (heading) {
      const text = heading[2].trim()
      blocks.push({
        kind: "heading",
        level: heading[1].length,
        text,
        slug: slugify(text),
      })
      i += 1
      continue
    }

    // A table needs its divider row: without that check a paragraph that
    // happens to start with a pipe would eat the lines under it.
    if (TABLE_ROW_RE.test(line) && TABLE_DIVIDER_RE.test(lines[i + 1] ?? "")) {
      const head = cells(line)
      const rows: string[][] = []
      i += 2
      while (i < lines.length && TABLE_ROW_RE.test(lines[i])) {
        rows.push(cells(lines[i]))
        i += 1
      }
      blocks.push({ kind: "table", head, rows })
      continue
    }

    if (BULLET_RE.test(line) || ORDERED_RE.test(line)) {
      const ordered = !BULLET_RE.test(line)
      const items: { depth: number; text: string }[] = []
      while (i < lines.length) {
        const cur = lines[i]
        const bullet = cur.match(BULLET_RE)
        const numbered = cur.match(ORDERED_RE)
        if (bullet) {
          items.push({ depth: Math.floor(bullet[1].length / 2), text: bullet[2] })
          i += 1
        } else if (numbered) {
          items.push({ depth: Math.floor(numbered[1].length / 2), text: numbered[3] })
          i += 1
        } else if (cur.trim() && items.length && !HEADING_RE.test(cur) && !cur.startsWith("```")) {
          // A wrapped continuation line belongs to the bullet above it.
          items[items.length - 1].text += ` ${cur.trim()}`
          i += 1
        } else {
          break
        }
      }
      blocks.push({ kind: "list", ordered, items })
      continue
    }

    const para: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !HEADING_RE.test(lines[i]) &&
      !lines[i].startsWith("```") &&
      !BULLET_RE.test(lines[i]) &&
      !ORDERED_RE.test(lines[i]) &&
      !TABLE_ROW_RE.test(lines[i]) &&
      !/^(-{3,}|_{3,}|\*{3,})\s*$/.test(lines[i])
    ) {
      para.push(lines[i])
      i += 1
    }
    if (para.length) blocks.push({ kind: "para", lines: para })
    else i += 1
  }

  return blocks
}

/** `**bold**`, `` `code` `` and `[text](href)` — nothing else is in the doc. */
const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g

function inline(text: string, keyPrefix: string) {
  return text.split(INLINE_RE).map((part, n) => {
    const key = `${keyPrefix}-${n}`
    if (!part) return null
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={key} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={key}
          className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {part.slice(1, -1)}
        </code>
      )
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (link) {
      return (
        <a
          key={key}
          href={link[2]}
          className="text-primary underline underline-offset-2"
          target="_blank"
          rel="noreferrer"
        >
          {link[1]}
        </a>
      )
    }
    return <span key={key}>{part}</span>
  })
}

const HEADING_CLASS: Record<number, string> = {
  1: "mt-2 text-3xl font-bold tracking-tight",
  2: "mt-12 scroll-mt-6 border-t pt-8 text-2xl font-bold tracking-tight",
  3: "mt-8 scroll-mt-6 text-lg font-semibold",
  4: "mt-6 scroll-mt-6 text-base font-semibold text-muted-foreground",
  5: "mt-4 scroll-mt-6 text-sm font-semibold text-muted-foreground",
  6: "mt-4 scroll-mt-6 text-sm font-semibold text-muted-foreground",
}

function Rendered({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((b, n) => {
        const key = `b${n}`
        switch (b.kind) {
          case "heading": {
            const Tag = `h${Math.min(b.level, 6)}` as "h1"
            return (
              <Tag key={key} id={b.slug} className={HEADING_CLASS[b.level]}>
                {inline(b.text, key)}
              </Tag>
            )
          }
          case "para":
            return (
              <p key={key} className="mt-4 leading-relaxed text-muted-foreground">
                {inline(b.lines.join(" "), key)}
              </p>
            )
          case "list": {
            const Tag = b.ordered ? "ol" : "ul"
            return (
              <Tag
                key={key}
                className={
                  "mt-4 space-y-1.5 text-muted-foreground " +
                  (b.ordered ? "list-decimal pl-5" : "list-disc pl-5")
                }
              >
                {b.items.map((item, m) => (
                  <li
                    key={`${key}-${m}`}
                    className="leading-relaxed"
                    style={item.depth ? { marginLeft: item.depth * 16 } : undefined}
                  >
                    {inline(item.text, `${key}-${m}`)}
                  </li>
                ))}
              </Tag>
            )
          }
          case "table":
            return (
              // Wide tables scroll inside themselves; the page never does.
              <div key={key} className="mt-5 overflow-x-auto rounded-lg border">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {b.head.map((h, m) => (
                        <th
                          key={`${key}-h${m}`}
                          className="border-b px-3 py-2 text-left font-semibold"
                        >
                          {inline(h, `${key}-h${m}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, m) => (
                      <tr key={`${key}-r${m}`} className="align-top">
                        {row.map((c, k) => (
                          <td
                            key={`${key}-r${m}c${k}`}
                            className="border-b px-3 py-2 text-muted-foreground last:border-r-0"
                          >
                            {inline(c, `${key}-r${m}c${k}`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          case "code":
            return (
              <pre
                key={key}
                className="mt-4 overflow-x-auto rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed"
              >
                <code className="font-mono">{b.text}</code>
              </pre>
            )
          case "rule":
            return <hr key={key} className="mt-10 border-0" />
        }
      })}
    </>
  )
}

export function GlossaryPage() {
  const blocks = useMemo(() => parse(glossaryMarkdown), [])
  // Only the numbered top-level sections — an h3 index on a doc this long is
  // longer than the thing it indexes.
  const toc = useMemo(
    () => blocks.filter((b): b is Extract<Block, { kind: "heading" }> => b.kind === "heading" && b.level === 2),
    [blocks],
  )

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <BookOpen className="size-5 shrink-0 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Glossary</h1>
        </div>
        <Link
          to="/"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border bg-background px-2.5 text-sm hover:bg-muted"
        >
          <ArrowLeft className="size-3.5" />
          Dashboard
        </Link>
      </div>

      <div className="flex flex-col-reverse gap-8 lg:flex-row">
        <article className="min-w-0 flex-1 text-sm">
          <Rendered blocks={blocks} />
        </article>

        <nav className="shrink-0 lg:sticky lg:top-6 lg:h-fit lg:w-64">
          <div className="rounded-lg border p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sections
            </div>
            <ul className="space-y-1 text-sm">
              {toc.map((h) => (
                <li key={h.slug}>
                  <a
                    href={`#${h.slug}`}
                    className="block rounded px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {h.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </div>
  )
}
